import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useColorMode } from '@chakra-ui/react'
import { getShareMeta, ApiError } from '@/lib/api/shares'
import { parseShareIdParam } from '@/lib/utils/share-url'
import {
  validateEmbedAccess,
  determineDecryptionPath,
  decryptShareContent,
  consumeShareQuietly,
  hasCryptoFields,
} from '@/lib/shares/load'
import type { ShareFormat } from '@core/types/share'

type EmbedState = 'loading' | 'decrypting' | 'viewing' | 'error'

/**
 * Manages the embed share lifecycle: loading metadata, validating access,
 * decrypting content (public or encrypted), and handling burn-after-read.
 */
export function useEmbedShare() {
  const { shareId: shareIdParam } = useParams<{ shareId: string }>()
  const shareId = shareIdParam ? parseShareIdParam(shareIdParam) : undefined
  const [searchParams] = useSearchParams()
  const { setColorMode, colorMode } = useColorMode()

  const [state, setState] = useState<EmbedState>('loading')
  const [content, setContent] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [format, setFormat] = useState<ShareFormat>('md')

  // Get passcode from hash
  const passcode = window.location.hash ? window.location.hash.slice(1) : null

  // Set theme from URL param
  useEffect(() => {
    const theme = searchParams.get('theme')
    if (theme === 'dark' || theme === 'light') {
      setColorMode(theme)
    }
  }, [searchParams, setColorMode])

  // Prevent body scroll for clean embed
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    document.body.style.margin = '0'
    document.body.style.padding = '0'
    return () => {
      document.body.style.overflow = ''
      document.body.style.margin = ''
      document.body.style.padding = ''
    }
  }, [])

  useEffect(() => {
    loadAndDecrypt()
  }, [shareId, passcode])

  const loadAndDecrypt = async () => {
    if (!shareId) return

    try {
      const meta = await getShareMeta(shareId)
      setFormat(meta.format)

      const embedAccess = validateEmbedAccess(meta)
      if (!embedAccess.valid) {
        setError(embedAccess.error)
        setState('error')
        return
      }

      // Encrypted share without crypto fields or passcode
      if (meta.cipher !== 'NONE' && !passcode) {
        setError('Passcode required')
        setState('error')
        return
      }

      if (meta.cipher !== 'NONE' && !hasCryptoFields(meta)) {
        setError('Invalid share metadata')
        setState('error')
        return
      }

      const path = determineDecryptionPath(meta, null, passcode)

      setState('decrypting')
      const decrypted = await decryptShareContent(shareId, meta, path)
      setContent(decrypted)
      setState('viewing')

      if (meta.burnAfterRead) {
        await consumeShareQuietly(shareId)
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Failed to load')
      }
      setState('error')
    }
  }

  return { state, content, error, format, colorMode }
}
